import { CREW_PHOTO_MAX_BYTES, CREW_PHOTO_MIME_TYPES } from '@felix/domain';
import type { CrewMedicalInput, CrewMedicalResult, CrewProfileView } from '@felix/ui';
import { ApiError, api } from './api.js';
import { tusUpload } from './tus-upload.js';

/**
 * Personel kartının fotoğrafı ve sağlık bilgisi (ADR 0158): fotoğraf tarayıcıda küçültülür, tus
 * ile yüklenir (amaç `crew_photo`; sunucu sınırı 5 MB, tür içerikten), sonra karta konur.
 */
export const CREW_PHOTO_UPLOAD_PURPOSE = 'crew_photo';
/** Küçültülen fotoğrafın uzun kenarı (px): kart ve liste avatarı için yeter. */
export const CREW_PHOTO_MAX_EDGE = 1024;
/** Bundan küçük ve kenarı sınırı aşmayan fotoğraf olduğu gibi gönderilir. */
const KEEP_BELOW_BYTES = 1024 * 1024;

const id = encodeURIComponent;

/** Küçültülmüş boyut: oran korunur, uzun kenar en çok `max`; küçükse aynı. */
export function photoTargetSize(
  width: number,
  height: number,
  max = CREW_PHOTO_MAX_EDGE,
): { width: number; height: number } {
  const edge = Math.max(width, height);
  if (edge <= max || edge === 0) return { width, height };
  const scale = max / edge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Fotoğrafı tarayıcıda küçültür (JPEG, %85). Desteklenmeyen tür, küçük dosya ya da tarayıcı
 * küçültemezse dosya olduğu gibi döner; tür ve boyutu sunucu ayrıca denetler.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (!(CREW_PHOTO_MIME_TYPES as readonly string[]).includes(file.type)) return file;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const target = photoTargetSize(bitmap.width, bitmap.height);
    if (target.width === bitmap.width && file.size <= KEEP_BELOW_BYTES) {
      bitmap.close();
      return file;
    }
    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, target.width, target.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    if (!blob || blob.size >= file.size) return file;
    const name = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}

/** Fotoğrafı koyar ya da (`null`) kaldırır. */
export async function setCrewPhoto(
  userId: string,
  rowVersion: number,
  file: File | null,
  owner?: string,
): Promise<CrewProfileView> {
  let upload: { uploadId: string; fileName: string } | null = null;
  if (file) {
    const photo = await shrinkPhoto(file);
    // Sunucu da denetler; büyük dosya hiç gönderilmez
    if (photo.size <= 0 || photo.size > CREW_PHOTO_MAX_BYTES) {
      throw new ApiError('FILE_TOO_LARGE', 413);
    }
    const uploadId = await tusUpload(photo, {
      purpose: CREW_PHOTO_UPLOAD_PURPOSE,
      ...(owner ? { owner } : {}),
      onProgress: () => undefined,
    });
    upload = { uploadId, fileName: photo.name };
  }
  return api<CrewProfileView>(`/office/crew/profiles/${id(userId)}/photo`, {
    method: 'PUT',
    body: { rowVersion, upload },
  });
}

/** Sağlık bilgisi: her okuma sunucuda kayda geçer (ADR 0158 §2). */
export function crewMedical(userId: string): Promise<CrewMedicalResult> {
  return api<CrewMedicalResult>(`/office/crew/profiles/${id(userId)}/medical`);
}

export function saveCrewMedical(
  userId: string,
  input: { medical: CrewMedicalInput; rowVersion: number | null },
): Promise<CrewMedicalResult> {
  return api<CrewMedicalResult>(`/office/crew/profiles/${id(userId)}/medical`, {
    method: 'PUT',
    body: input,
  });
}
